/**
 * «Дни» обзвона: как отработали сделки утреннего снимка. Вкладка `?tab=days`
 * на `/dialing` и карточка дня `/dialing/day/<ГГГГ-ММ-ДД>`. Спека
 * `docs/specs/2026-09-26-dialing-days-design.md`; числа — те же, что у
 * `prokat-runner deals-days`. Только своя база: снимки, `bitrix_call`, статус разговора.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";

import { bitrixUrl } from "@/lib/calls";
import { ATTEMPTS, type DayCard, type DayDeal, type Metrics, loadDay, loadDays, pct, talkCallsUrl } from "@/lib/dialingDays";
import { shortTime } from "@/lib/leads";

const dm = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
const hm = (iso: string | null) => (iso ? iso.slice(11, 16) : "—");
const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle";
const th = "px-3 py-2 text-left font-medium";
const td = "whitespace-nowrap px-3 py-1.5 tabular-nums";

/** Число и доля от когорты мелко. */
function Num({ n, of, tone = "" }: { n: number; of?: number; tone?: string }) {
  return (
    <span className={tone}>
      {n}
      {of !== undefined && <span className="ml-1 text-[11px] text-fg-faint">{pct(n, of)}</span>}
    </span>
  );
}

/** «С какой попытки разговор»: 1-я · 2-я · 3+ · входящий — стоит ли перезванивать. */
function FunnelShort({ m }: { m: Metrics }) {
  return (
    <span title={`Попыток всего ${m.attempts}. Разговор на 1-й, 2-й, 3-й и дальше исходящей; «вх» — клиент перезвонил сам`}>
      {ATTEMPTS.map((a, i) => (
        <span key={a} className={i ? "ml-2" : ""}>
          <span className="text-[11px] text-fg-faint">{a === "3+" ? "3+" : `${a}-я`} </span>
          <span className={m.funnel[a].talked ? "text-ok" : "text-fg-faint"}>{m.funnel[a].talked}</span>
        </span>
      ))}
      {m.talk_inbound > 0 && (
        <span className="ml-2">
          <span className="text-[11px] text-fg-faint">вх </span>
          {m.talk_inbound}
        </span>
      )}
    </span>
  );
}

const cellLink = "rounded-[var(--radius-ui)] hover:underline";

export function DaysTab() {
  const [type, setType] = useState<number | null>(null);
  const data = useQuery({ queryKey: ["deals-days"], queryFn: loadDays });
  if (data.isPending) return <div className="text-[12.5px] text-fg-muted">Считаю дни…</div>;
  if (data.isError) return <div className="text-[12.5px] text-err">Не удалось получить дни: {String(data.error.message)}</div>;
  const r = data.data;
  const types = r.types.filter((t) => r.days.some((d) => d.by_type[t.id]));
  const pick = (d: (typeof r.days)[number]): Metrics | null => (type === null ? d.total : (d.by_type[type] ?? null));

  return (
    <section className={card} data-annot="дни обзвона">
      <div className="flex flex-wrap items-center gap-1 border-b border-border p-2">
        {[{ id: null as number | null, name: "Все типы" }, ...types].map((t) => (
          <button
            key={t.id ?? "all"}
            type="button"
            onClick={() => setType(t.id)}
            className={`rounded-[var(--radius-ui)] border px-2.5 py-1 text-[12.5px] hover:bg-bg-hover ${type === t.id ? "border-fg" : "border-transparent text-fg-muted"}`}
          >
            {t.name}
          </button>
        ))}
        <span className="ml-auto px-2 text-[11.5px] text-fg-faint">
          Когорта — сделки утреннего снимка (05:00); доли — от неё. Разговор — по расшифровке, не код 200.
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-fg-muted">
            <tr className="border-b border-border">
              <th className={th}>День</th>
              <th className={th}>Утром</th>
              <th className={th} title="Вечерний снимок 22:00 и сделки, пришедшие днём">Вечером / пришло</th>
              <th className={th}>Обзвонили</th>
              <th className={th} title="Был разговор с клиентом по расшифровке; клик — эти звонки в «Звонках»">Разговор</th>
              <th className={th} title="На какой исходящей попытке состоялся разговор">Разговор с попытки</th>
              <th className={th}>Не дозвонились</th>
              <th className={th}>Не трогали</th>
              <th className={th} title="Сменилась стадия; клик — куда перевели">Перевели</th>
              <th className={th} title="Ушла из обзвона без смены стадии — перенесли дату обработки">Перенесли</th>
            </tr>
          </thead>
          <tbody>
            {r.days.map((d) => {
              const m = pick(d);
              return (
                <tr key={d.date} className="border-b border-border last:border-0 hover:bg-bg-hover">
                  <td className={td}>
                    <Link to={`/dialing/day/${d.date}${type === null ? "" : `?type=${type}`}`} className="text-accent hover:underline">
                      {dm(d.date)}
                    </Link>
                    {!d.closed && <span className="ml-2 text-[11px] text-warn">не закрыт</span>}
                  </td>
                  {m === null ? (
                    <td className={`${td} text-fg-faint`} colSpan={9}>
                      сделок этого типа утром не было
                    </td>
                  ) : (
                    <>
                      <td className={td}>{m.deals}</td>
                      <td className={td}>{d.closed && type === null ? `${d.evening} / ${d.new}` : "—"}</td>
                      <td className={td}><Num n={m.called} of={m.deals} /></td>
                      <td className={td}>
                        <Link to={talkCallsUrl(d.date, type)} className={cellLink} title="звонки с разговором по сделкам когорты">
                          <Num n={m.talked} of={m.deals} tone="text-ok" />
                        </Link>
                      </td>
                      <td className={td}><FunnelShort m={m} /></td>
                      <td className={td}><Num n={m.no_answer} of={m.deals} /></td>
                      <td className={td}><Num n={m.untouched} of={m.deals} tone={m.untouched ? "text-warn" : ""} /></td>
                      <td className={td}>
                        <Link to={`/dialing/day/${d.date}?${type === null ? "" : `type=${type}&`}show=moved`} className={cellLink} title="сделки, которым сменили стадию, и куда">
                          <Num n={m.moved} of={m.deals} />
                        </Link>
                      </td>
                      <td className={td}>{d.closed ? <Num n={m.rescheduled} of={m.deals} /> : "—"}</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!r.days.length && <div className="p-4 text-[12.5px] text-fg-muted">Снимков обзвона ещё нет.</div>}
    </section>
  );
}

/** Фильтр списка сделок карточки — в адресе (`?type=&show=&to=&caller=`). */
type Show = "called" | "talk" | "no_answer" | "untouched" | "moved" | "rescheduled" | "gone";
type Filter = { type: number | null; show: Show | null; to: string | null; caller: string | null };

function useFilter() {
  const [params, setParams] = useSearchParams();
  const f: Filter = {
    type: params.get("type") === null ? null : Number(params.get("type")),
    show: (params.get("show") as Show | null) ?? null,
    to: params.get("to"),
    caller: params.get("caller"),
  };
  const set = (n: Filter) => {
    const q: Record<string, string> = {};
    if (n.type !== null) q.type = String(n.type);
    if (n.show) q.show = n.show;
    if (n.to) q.to = n.to;
    if (n.caller) q.caller = n.caller;
    setParams(q, { replace: true });
  };
  return [f, set] as const;
}

const keep = (f: Filter) => (d: DayDeal) =>
  (f.type === null || d.type_id === f.type) &&
  (f.to === null || (d.moved && d.stage_evening === f.to)) &&
  (f.caller === null || d.callers.includes(f.caller)) &&
  (f.show === null ||
    (f.show === "called" && d.attempts > 0) ||
    (f.show === "talk" && d.result === "talk") ||
    (f.show === "no_answer" && d.result === "no_answer") ||
    (f.show === "untouched" && d.result === "untouched") ||
    (f.show === "moved" && d.moved) ||
    (f.show === "rescheduled" && !!d.gone && !d.moved) ||
    (f.show === "gone" && !!d.gone));

const NONE: Filter = { type: null, show: null, to: null, caller: null };
const RESULT: Record<DayDeal["result"], [string, string]> = {
  talk: ["разговор", "text-ok"],
  no_answer: ["не дозвонились", "text-fg-muted"],
  untouched: ["не трогали", "text-warn"],
};

function Tiles({ r, f, set }: { r: DayCard; f: Filter; set: (f: Filter) => void }) {
  const t = r.total;
  const tiles: { label: string; value: string | number; hint: string; show: Show | null; tone?: string }[] = [
    { label: "Утром в обзвоне", value: t.deals, hint: `снимок ${hm(r.morning_at)}`, show: null },
    { label: "Обзвонили", value: t.called, hint: `${pct(t.called, t.deals)} · попыток ${t.attempts}`, show: "called" },
    { label: "Разговор", value: t.talked, hint: `${pct(t.talked, t.deals)} · соединились ${t.connected}`, show: "talk", tone: "text-ok" },
    { label: "Не дозвонились", value: t.no_answer, hint: `${pct(t.no_answer, t.deals)} от утра`, show: "no_answer" },
    { label: "Не трогали", value: t.untouched, hint: `${pct(t.untouched, t.deals)} — ни одной попытки`, show: "untouched", tone: t.untouched ? "text-warn" : "" },
    { label: "Перевели", value: t.moved, hint: "стадия сменилась", show: "moved" },
    { label: "Перенесли", value: r.closed ? t.rescheduled : "—", hint: "ушла без смены стадии", show: r.closed ? "rescheduled" : null },
    {
      label: "Вечером",
      value: r.closed ? (r.evening ?? 0) : "—",
      hint: r.closed ? `снимок ${hm(r.evening_at)} · ушло ${t.gone} · пришло ${r.new}` : "день не закрыт",
      show: r.closed ? "gone" : null,
    },
  ];
  return (
    <section className={`${card} p-2`} data-annot="итоги дня обзвона">
      <div className="grid grid-cols-2 gap-1 md:grid-cols-4">
        {tiles.map((x) => (
          <button
            key={x.label}
            type="button"
            onClick={() => set({ ...NONE, type: f.type, show: x.show })}
            className={`flex min-w-0 flex-col gap-1 rounded-[var(--radius-ui)] border p-3 text-left hover:bg-bg-hover ${f.show === x.show && !f.to && !f.caller ? "border-fg" : "border-transparent"}`}
          >
            <span className="text-[12px] text-fg-muted">{x.label}</span>
            <span className={`text-[26px] font-semibold leading-none tracking-[-0.02em] tabular-nums ${x.tone ?? ""}`}>{x.value}</span>
            <span className="text-[11.5px] text-fg-faint">{x.hint}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

/** Метрики выбранного типа (или всей когорты) — для воронки и «куда перевели». */
const picked = (r: DayCard, f: Filter) => (f.type === null ? r.total : (r.by_type.find((t) => t.type_id === f.type) ?? r.total));

function Funnel({ r, f }: { r: DayCard; f: Filter }) {
  const m = picked(r, f);
  const row = "border-b border-border last:border-0";
  return (
    <section className={card} data-annot="воронка попыток">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2.5">
        <h2 className="text-[13px] font-semibold">С какой попытки разговор</h2>
        {f.type !== null && <span className="text-[12px] text-fg-muted">{r.by_type.find((t) => t.type_id === f.type)?.name}</span>}
        <Link to={talkCallsUrl(r.date, f.type)} className="ml-auto text-[12px] text-accent hover:underline">
          звонки с разговором →
        </Link>
      </div>
      <table className="w-full text-[12.5px]">
        <thead className="text-fg-muted">
          <tr className="border-b border-border">
            <th className={th}>Попытка</th>
            <th className={th} title="Сделок, по которым дошли до этой попытки без разговора раньше">Дошли</th>
            <th className={th} title="Впервые соединились (код 200) на этой попытке">Соединились</th>
            <th className={th} title="Разговор по расшифровке состоялся на этой попытке">Разговор</th>
            <th className={th}>Разговор от дошедших</th>
          </tr>
        </thead>
        <tbody>
          {ATTEMPTS.map((a) => {
            const x = m.funnel[a];
            return (
              <tr key={a} className={row}>
                <td className={td}>{a === "3+" ? "3-я и дальше" : `${a}-я`}</td>
                <td className={td}>{x.reached}</td>
                <td className={td}>{x.connected}</td>
                <td className={`${td} ${x.talked ? "text-ok" : "text-fg-faint"}`}>{x.talked}</td>
                <td className={td}>{pct(x.talked, x.reached)}</td>
              </tr>
            );
          })}
          <tr className={row}>
            <td className={`${td} text-fg-muted`}>клиент перезвонил сам</td>
            <td className={td} />
            <td className={td} />
            <td className={`${td} ${m.talk_inbound ? "text-ok" : "text-fg-faint"}`}>{m.talk_inbound}</td>
            <td className={td} />
          </tr>
        </tbody>
      </table>
      <div className="border-t border-border px-4 py-2 text-[11.5px] text-fg-faint">
        «3-я и дальше» — сделки, дошедшие до 3-й попытки; разговор — на любой из них. Попыток всего {m.attempts}.
      </div>
    </section>
  );
}

function MovedTo({ r, f, set }: { r: DayCard; f: Filter; set: (f: Filter) => void }) {
  const m = picked(r, f);
  const to = Object.entries(m.to).sort((a, b) => b[1] - a[1]);
  return (
    <section className={card} data-annot="куда перевели за день">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2.5">
        <h2 className="text-[13px] font-semibold">Куда перевели</h2>
        <span className="text-[12px] text-fg-muted">стадия вечером · {m.moved} из {m.deals}</span>
      </div>
      <table className="w-full text-[12.5px]">
        <tbody>
          {to.map(([code, n]) => (
            <tr key={code} className="border-b border-border last:border-0">
              <td className={td}>
                <button
                  type="button"
                  onClick={() => set({ ...NONE, type: f.type, to: code })}
                  className={`rounded-[var(--radius-ui)] px-1 text-left hover:bg-bg-hover ${f.to === code ? "ring-1 ring-fg" : ""}`}
                >
                  {r.stages[code] ?? code}
                </button>
              </td>
              <td className={`${td} text-right`}>
                <Num n={n} of={m.moved} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!to.length && <div className="p-4 text-[12.5px] text-fg-muted">Стадию никому не меняли.</div>}
    </section>
  );
}

function Types({ r, f, set }: { r: DayCard; f: Filter; set: (f: Filter) => void }) {
  const cell = (type: number, show: Show, n: number, of: number, tone = "") => (
    <button
      type="button"
      onClick={() => set({ ...NONE, type, show })}
      className={`rounded-[var(--radius-ui)] px-1 hover:bg-bg-hover ${f.type === type && f.show === show && !f.to ? "ring-1 ring-fg" : ""}`}
    >
      <Num n={n} of={of} tone={tone} />
    </button>
  );
  return (
    <section className={card} data-annot="типы обзвона за день">
      <h2 className="border-b border-border px-4 py-2.5 text-[13px] font-semibold">Типы обзвона: что сделали и куда перевели</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-fg-muted">
            <tr className="border-b border-border">
              <th className={th}>Тип</th>
              <th className={th}>Утром</th>
              <th className={th}>Обзвонили</th>
              <th className={th}>Разговор</th>
              <th className={th}>Не дозвон.</th>
              <th className={th}>Не трогали</th>
              <th className={th}>Попыток</th>
              <th className={th}>Перевели</th>
              <th className={th}>Перенесли</th>
              <th className={th}>Куда перевели (стадия вечером)</th>
            </tr>
          </thead>
          <tbody>
            {r.by_type.map((t) => (
              <tr key={t.type_id} className="border-b border-border align-top last:border-0">
                <td className={td}>
                  <button type="button" onClick={() => set({ ...NONE, type: t.type_id })} className="hover:underline">
                    {t.name}
                  </button>
                </td>
                <td className={td}>{t.deals}</td>
                <td className={td}>{cell(t.type_id, "called", t.called, t.deals)}</td>
                <td className={td}>{cell(t.type_id, "talk", t.talked, t.deals, "text-ok")}</td>
                <td className={td}>{cell(t.type_id, "no_answer", t.no_answer, t.deals)}</td>
                <td className={td}>{cell(t.type_id, "untouched", t.untouched, t.deals, t.untouched ? "text-warn" : "")}</td>
                <td className={td}>{t.attempts}</td>
                <td className={td}>{cell(t.type_id, "moved", t.moved, t.deals)}</td>
                <td className={td}>{r.closed ? cell(t.type_id, "rescheduled", t.rescheduled, t.deals) : "—"}</td>
                <td className="px-3 py-1.5">
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(t.to)
                      .sort((a, b) => b[1] - a[1])
                      .map(([code, n]) => (
                        <button
                          key={code}
                          type="button"
                          onClick={() => set({ ...NONE, type: t.type_id, to: code })}
                          className={`rounded-[var(--radius-ui)] border border-border px-1.5 text-[11.5px] hover:bg-bg-hover ${f.type === t.type_id && f.to === code ? "ring-1 ring-fg" : ""}`}
                        >
                          {r.stages[code] ?? code} <span className="tabular-nums text-fg-muted">{n}</span>
                        </button>
                      ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Users({ r, f, set }: { r: DayCard; f: Filter; set: (f: Filter) => void }) {
  return (
    <section className={card} data-annot="сотрудники за день">
      <h2 className="border-b border-border px-4 py-2.5 text-[13px] font-semibold">Сотрудники: сделки когорты</h2>
      <table className="w-full text-[12.5px]">
        <thead className="text-fg-muted">
          <tr className="border-b border-border">
            <th className={th}>Сотрудник</th>
            <th className={th} title="Разных сделок с исходящим">Сделок</th>
            <th className={th}>Попыток</th>
            <th className={th}>Соединились</th>
            <th className={th} title="Звонков с разговором по расшифровке">Разговоров</th>
            <th className={th} title="Из его сделок сменили стадию">Перевёл</th>
          </tr>
        </thead>
        <tbody>
          {r.users.map((u) => (
            <tr key={u.bitrix_user_id} className="border-b border-border last:border-0">
              <td className={td}>
                <button
                  type="button"
                  onClick={() => set({ ...NONE, caller: u.name })}
                  className={`rounded-[var(--radius-ui)] px-1 hover:bg-bg-hover ${f.caller === u.name ? "ring-1 ring-fg" : ""}`}
                >
                  {u.name}
                </button>
              </td>
              <td className={td}>{u.deals}</td>
              <td className={td}>{u.attempts}</td>
              <td className={td}>{u.connected}</td>
              <td className={td}>
                <Num n={u.talks} tone={u.talks ? "text-ok" : "text-warn"} />
                <span className="ml-1 text-[11px] text-fg-faint">{pct(u.talks, u.attempts)} попыток</span>
              </td>
              <td className={td}>{u.moved}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!r.users.length && <div className="p-4 text-[12.5px] text-fg-muted">По сделкам когорты звонков не было.</div>}
    </section>
  );
}

function Hours({ r }: { r: DayCard }) {
  if (!r.hours.length) return null;
  const row = (label: string, k: "attempts" | "talks" | "moves", tone = "") => (
    <tr className="border-b border-border last:border-0">
      <td className={`${td} text-fg-muted`}>{label}</td>
      {r.hours.map((h) => (
        <td key={h.hour} className={`${td} text-right ${h[k] ? tone : "text-fg-faint"}`}>
          {h[k] || "·"}
        </td>
      ))}
    </tr>
  );
  return (
    <section className={card} data-annot="день по часам">
      <h2 className="border-b border-border px-4 py-2.5 text-[13px] font-semibold">По часам (МСК)</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-fg-muted">
            <tr className="border-b border-border">
              <th className={th} />
              {r.hours.map((h) => (
                <th key={h.hour} className={`${th} text-right`}>
                  {String(h.hour).padStart(2, "0")}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {row("Попыток", "attempts")}
            {row("Разговоров", "talks", "text-ok")}
            {row("Смен стадии", "moves")}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Deals({ r, f, set }: { r: DayCard; f: Filter; set: (f: Filter) => void }) {
  const rows = r.deals.filter(keep(f));
  const typeName = Object.fromEntries(r.types.map((t) => [t.id, t.name]));
  const active = f.type !== null || f.show || f.to || f.caller;
  return (
    <section className={card} data-annot="сделки дня">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2.5">
        <h2 className="text-[13px] font-semibold">Сделки</h2>
        <span className="text-[12px] text-fg-muted">{rows.length} из {r.deals.length}</span>
        {active && (
          <button type="button" onClick={() => set(NONE)} className="ml-auto text-[12px] text-accent hover:underline">
            сбросить фильтр
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-fg-muted">
            <tr className="border-b border-border">
              <th className={th}>Сделка</th>
              <th className={th}>Тип утром</th>
              <th className={th}>Стадия утром → вечером</th>
              <th className={th}>Итог</th>
              <th className={th}>Попыток</th>
              <th className={th}>Соедин.</th>
              <th className={th}>Разговоров</th>
              <th className={th}>Последний звонок</th>
              <th className={th}>Звонили</th>
              <th className={th}>Ответственный</th>
              <th className={th}>Вечером</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 500).map((d) => {
              const [label, tone] = RESULT[d.result];
              return (
                <tr key={d.id} className="border-b border-border last:border-0 hover:bg-bg-hover">
                  <td className={td}>
                    <a href={bitrixUrl("deal", d.id)} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                      {d.id}
                    </a>
                    <Link to={`/deals/${d.id}`} className="ml-1.5 text-[11.5px] text-accent hover:underline" title="карточка сделки: вся хронология">
                      лента
                    </Link>
                  </td>
                  <td className={td}>{typeName[d.type_id]}</td>
                  <td className={td}>
                    <span className="text-fg-muted">{r.stages[d.stage_morning] ?? d.stage_morning}</span>
                    {d.moved && <span className="font-medium"> → {r.stages[d.stage_evening] ?? d.stage_evening}</span>}
                  </td>
                  <td className={`${td} ${tone}`}>{label}</td>
                  <td className={td}>{d.attempts || "—"}</td>
                  <td className={td}>{d.connected || "—"}</td>
                  <td className={td}>
                    {d.talks || "—"}
                    {d.pending > 0 && <span className="ml-1 text-[11px] text-fg-faint" title="Код 200 без разметки">+{d.pending}?</span>}
                  </td>
                  <td className={td}>{shortTime(d.last_call_at)}</td>
                  <td className="px-3 py-1.5 text-[12px]">{d.callers.join(", ") || "—"}</td>
                  <td className={td}>{d.user || <span className="text-fg-faint">—</span>}</td>
                  <td className={td}>{d.gone === null ? "—" : d.gone ? (d.moved ? "ушла" : "перенесли") : "осталась"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.length > 500 && <div className="p-3 text-[12px] text-fg-muted">Показаны первые 500 — сузьте фильтр.</div>}
    </section>
  );
}

export function DialingDayPage() {
  const { day = "" } = useParams();
  const [f, set] = useFilter();
  const data = useQuery({ queryKey: ["deals-day", day], queryFn: () => loadDay(day), enabled: /^\d{4}-\d{2}-\d{2}$/.test(day) });

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="flex flex-wrap items-center gap-3" data-annot="шапка дня обзвона">
        <Link to="/dialing?tab=days" className="text-[12.5px] text-accent hover:underline">
          ← Дни
        </Link>
        <h1 className="text-[16px] font-semibold tracking-[-0.015em]">Обзвон за {day ? dm(day) : "—"}</h1>
        {data.data && !data.data.closed && <span className="text-[12px] text-warn">день не закрыт — вечерний снимок в 22:00</span>}
      </div>
      {data.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Считаю день…</div>
      ) : data.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить день: {String(data.error.message)}</div>
      ) : (
        <>
          <Tiles r={data.data} f={f} set={set} />
          <div className="grid gap-4 xl:grid-cols-2">
            <Funnel r={data.data} f={f} />
            <MovedTo r={data.data} f={f} set={set} />
          </div>
          <Types r={data.data} f={f} set={set} />
          <div className="grid gap-4 xl:grid-cols-2">
            <Users r={data.data} f={f} set={set} />
            <Hours r={data.data} />
          </div>
          <Deals r={data.data} f={f} set={set} />
        </>
      )}
    </main>
  );
}
